import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";
import { EnvironmentAuthorizationError } from "./auth.ts";
import { ProviderInstanceId } from "./providerInstance.ts";

const AccountIdentity = Schema.Struct({
  email: Schema.NullOr(Schema.String),
  planType: Schema.String,
});
export const CodexAccountConnectionState = Schema.Union([
  Schema.Struct({ state: Schema.Literal("unavailable"), reason: Schema.String }),
  Schema.Struct({
    state: Schema.Literal("disconnected"),
    revocationConfirmed: Schema.Boolean,
  }),
  Schema.Struct({
    state: Schema.Literal("plan-connected"),
    account: AccountIdentity,
    usageEnabled: Schema.Boolean,
  }),
  Schema.Struct({ state: Schema.Literals(["idle", "starting", "verifying", "cancelling"]) }),
  Schema.Struct({
    state: Schema.Literal("authorizing"),
    method: Schema.Literals(["browser", "device-code"]),
    url: Schema.String,
    userCode: Schema.optionalKey(Schema.String),
  }),
  Schema.Struct({ state: Schema.Literal("connected"), account: AccountIdentity }),
  // Codex holds an OpenAI API key for this account (API billing, no ChatGPT
  // plan). Distinct from "connected", whose account is a ChatGPT identity.
  Schema.Struct({ state: Schema.Literal("api-key-connected") }),
  Schema.Struct({
    state: Schema.Literal("cancelled"),
    account: Schema.optionalKey(AccountIdentity),
  }),
  Schema.Struct({ state: Schema.Literal("failed"), reason: Schema.String }),
]);
export type CodexAccountConnectionState = typeof CodexAccountConnectionState.Type;
export const CodexAccountCommand = Schema.Struct({
  instanceId: ProviderInstanceId,
  // "api-key-sign-in" hands Codex an OpenAI API key through its own login
  // method (app-server `account/login/start`, type "apiKey"); Codex keeps it in
  // its own protected store for this account's home. It completes in the call.
  action: Schema.Literals(["start", "status", "cancel", "disconnect", "api-key-sign-in"]),
  method: Schema.optionalKey(Schema.Literals(["browser", "device-code"])),
  // Required by, and only accepted with, "api-key-sign-in". Redacted once
  // decoded, so no log, span or error formats the value.
  apiKey: Schema.optionalKey(Schema.RedactedFromValue(Schema.String.check(Schema.isNonEmpty()))),
});
export type CodexAccountCommand = typeof CodexAccountCommand.Type;
export class CodexAccountError extends Schema.TaggedErrorClass<CodexAccountError>()(
  "CodexAccountError",
  {
    message: Schema.String,
  },
) {}
export const CODEX_ACCOUNT_RPC = "codexAccount.command";
export const CodexAccountRpcGroup = RpcGroup.make(
  Rpc.make(CODEX_ACCOUNT_RPC, {
    payload: CodexAccountCommand,
    success: CodexAccountConnectionState,
    error: Schema.Union([CodexAccountError, EnvironmentAuthorizationError]),
  }),
);
